import { action, makeObservable, observable, runInAction } from "mobx";
import { MrbBaseListViewStore } from "mrb/core";
import { sideScroll } from "common/utils";
import { httpStatusCodes } from "mrb/core/constants";
import { MrbGridViewStore } from "mrb/components/grid";
import { MrbSimpleSelectStore } from "mrb/components/select";
import { HomeGenericSearchFilter } from "application/home/filters";
import { podcastType, podcastContentType } from "common/constants";
import { find, isNil, forEach } from "lodash";
import { HomeGridItemDecorator } from "application/home/models";
import { UserContentService } from "common/services";

class CategoryGenericSearchViewStore extends MrbBaseListViewStore {
    @observable showSearchView = false;
    @observable.ref appearsInData = [];
    @observable isAppearsInScrollable = false;
    @observable.ref appearsInContainerRef = null;
    _isInitialLoad = true;

    _abortController = null;

    orderOptions = [
        {
            label: "APPLICATION.HOME.LIST.GENERIC_SEARCH.ORDER.RELEVANCE_OPTION",
            value: "-1",
        },
        {
            label: "APPLICATION.HOME.LIST.GENERIC_SEARCH.ORDER.RECENTLY_ADDED_OPTION",
            value: "dateCreated",
        },
        {
            label: "APPLICATION.HOME.LIST.GENERIC_SEARCH.ORDER.ALPHABETICALY_OPTION",
            value: "title",
        },
    ];

    constructor(rootStore) {
        super(rootStore, {
            queryConfig: {
                filter: new HomeGenericSearchFilter(),
                disableUpdateQueryParams: true,
                pageSize: 30,
            },
        });
        makeObservable(this);
        this.service = this.rootStore.createApplicationService(UserContentService);

        this.setGridStore(
            new MrbGridViewStore(this.rootStore, this.queryUtility, {
                onCellClick: (item) => this.onClickCellItem(item),
                generateKey: (item) => item.stationId || item.podcastId,
                mapper: (item) => {
                    return new HomeGridItemDecorator({
                        ...item,
                        coverImageUrl: this.createCoverImageUrl(item),
                    });
                },
            })
        );

        this.orderBySelectStore = new MrbSimpleSelectStore(
            {
                textKey: "label",
                valueKey: "value",
                actions: {
                    onChange: (selectedOrder) => {
                        if (this.queryUtility.filter.orderBy !== selectedOrder.value) {
                            if (selectedOrder.value === "-1") {
                                this.queryUtility.filter.orderBy = null;
                                this.queryUtility.filter.orderDirection = null;
                            } else {
                                this.queryUtility.filter.orderBy = selectedOrder.value;
                                if (selectedOrder.value === "title") {
                                    this.queryUtility.filter.orderDirection = "asc";
                                } else {
                                    this.queryUtility.filter.orderDirection = "desc";
                                }
                            }
                            this.queryUtility.fetch();
                        }
                    },
                },
            },
            this.orderOptions
        );

        this.initializeSortingDefault();

        this.reaction(
            () => (this.queryUtility.filter.search || "").trim(),
            (searchPhrase) => {
                if (searchPhrase) {
                    this.queryUtility.fetch();
                } else {
                    this.reset();
                }
            },
            {
                delay: 300,
            }
        );

        this.reaction(
            () => ({
                data: this.appearsInData,
                container: this.appearsInContainerRef,
            }),
            (context) => {
                if (context.container) {
                    setTimeout(() => {
                        runInAction(() => {
                            this.isAppearsInScrollable = context.container.scrollWidth > context.container.clientWidth;
                        });
                    });
                }
            }
        );
    }

    async fetchResources(filter) {
        if (filter.search) {
            this.abortExistingRequest();
            this.setShowSearchView(true);
            let delay = null;
            if (this._isInitialLoad) {
                delay = 0;
            }
            this.gridStore.suspend(delay);
            filter.deviceId = this.rootStore.userDeviceStore.selectedDevice?.id;
            try {
                const response = await this.find(filter);
                runInAction(() => {
                    this.gridStore.setData(response);
                    this.setAppearsInData(response);
                    this.gridStore.resume();
                });
            } catch (err) {
                runInAction(() => {
                    this.gridStore.resume();
                });
                this.onFetchError(err);
            } finally {
                this._isInitialLoad = false;
            }
        } else {
            this.reset();
        }
    }

    initializeSortingDefault() {
        if (this.queryUtility.filter.orderBy) {
            this.orderBySelectStore.setSelectedItem(
                find(this.orderOptions, (orderOption) => orderOption.value === this.queryUtility.filter.orderBy)
            );
        } else {
            this.orderBySelectStore.setSelectedItem(this.orderOptions[0]);
        }
    }

    find(params) {
        this._abortController = new AbortController();
        return this.search(params, this._abortController);
    }

    async search(params, abortController) {
        const response = await this.service.genericSearch(params, abortController);
        return response.data;
    }

    onClickCellItem = (item) => {
        if (item.stationId) {
            this.rootStore.routerStore.goTo("master.application.home.station", {
                id: item.stationId,
            });
        } else if (item.podcastId) {
            if (item.podcastType === podcastType.podcast) {
                if (item.podcastContentType === podcastContentType.torahAnytime) {
                    this.rootStore.routerStore.goTo("master.application.home.torah-anytime", { id: item.podcastId });
                } else if (!isNil(item.parentPodcastId)) {
                    this.rootStore.routerStore.goTo(
                        "master.application.home.podcast-group",
                        {
                            id: item.parentPodcastId,
                        },
                        { subPodcastId: item.podcastId }
                    );
                } else {
                    this.rootStore.routerStore.goTo("master.application.home.podcast", {
                        id: item.podcastId,
                    });
                }
            } else {
                this.rootStore.routerStore.goTo("master.application.home.podcast-group", {
                    id: item.podcastId,
                });
            }
        } else if (item.albumId) {
            this.rootStore.routerStore.goTo(
                "master.application.naki-nigun.artist",
                {
                    id: item.artistId,
                },
                { albumId: item.albumId }
            );
        }
    };

    createPodcastGroupCoverImageUrl(coverImageId) {
        if (coverImageId) {
            return this.rootStore.coverImageFactory.createPodcastCoverImageUrl(coverImageId, 200, 200);
        }
        return null;
    }

    createArtistCoverImageUrl(artistCoverImageId) {
        if (artistCoverImageId) {
            return this.rootStore.coverImageFactory.createNakiNigunArtistCoverImageUrl(artistCoverImageId, 200, 200);
        }
        return null;
    }

    createCoverImageUrl(item) {
        if (item.coverImageId) {
            if (item.stationId) {
                return this.rootStore.coverImageFactory.createStationCoverImageUrl(item.coverImageId, 200, 200);
            } else if (item.podcastId) {
                return this.rootStore.coverImageFactory.createPodcastCoverImageUrl(item.coverImageId, 200, 200);
            } else if (item.albumId) {
                return this.rootStore.coverImageFactory.createNakiNigunAlbumCoverImageUrl(item.coverImageId, 200, 200);
            }
        }
        return null;
    }

    onFetchError(error) {
        if (error && error.statusCode === httpStatusCodes.CanceledRequest) {
            return;
        }
        this.rootStore.notificationStore.error(
            "Unexpected error occurred while trying to load content. Please contact the support team.",
            error
        );
    }

    @action.bound
    setAppearsInData(response) {
        const data = response.item ? response.item : response;

        const seenArtistIds = new Set();
        const seenPodcastIds = new Set();

        const mergedItems = [];

        forEach(data, (item) => {
            if (!isNil(item.artistId) && !seenArtistIds.has(item.artistId)) {
                mergedItems.push({
                    artistId: item.artistId,
                    name: item.artistName,
                    isArtistPremium: item.isArtistPremium,
                    coverImageUrl: this.createArtistCoverImageUrl(item.artistCoverImageId),
                });
                seenArtistIds.add(item.artistId);
            } else if (!isNil(item.parentPodcastId) && !seenPodcastIds.has(item.parentPodcastId)) {
                mergedItems.push({
                    podcastId: item.parentPodcastId,
                    title: item.parentPodcastTitle,
                    isParentPodcastPremium: item.isParentPodcastPremium,
                    coverImageUrl: this.createPodcastGroupCoverImageUrl(item.parentPodcastCoverImageId),
                });
                seenPodcastIds.add(item.parentPodcastId);
            }
        });
        this.appearsInData = mergedItems;
    }

    @action.bound
    setShowSearchView(value) {
        this.showSearchView = value;
    }

    @action.bound
    reset() {
        this.setShowSearchView(false);
        this.abortExistingRequest();
        this.gridStore.setData([]);
        this.setAppearsInData([]);
        this._isInitialLoad = true;
    }

    abortExistingRequest() {
        if (this._abortController) {
            this._abortController.abort();
        }
    }

    @action.bound
    scrollFeaturedLeft() {
        sideScroll(this.appearsInContainerRef, "left", 20, 400, 20);
    }

    @action.bound
    scrollFeaturedRight() {
        sideScroll(this.appearsInContainerRef, "right", 20, 400, 20);
    }

    onDestroy() {
        this.abortExistingRequest();
    }

    onClickPodcastGroup = (podcastGroup) => {
        this.rootStore.routerStore.goTo("master.application.home.podcast-group", {
            id: podcastGroup.podcastId,
        });
    };

    onClickArtist = (artist) => {
        this.rootStore.routerStore.goTo("master.application.naki-nigun.artist", {
            id: artist.artistId,
        });
    };

    @action.bound
    setAppearsInContainerRef(ref) {
        this.appearsInContainerRef = ref;
    }
}

export default CategoryGenericSearchViewStore;
